<template lang="pug">
form.flex.flex-col.smx-form-container(
  onsubmit="return false",
  :style="{ 'max-width': formWidth }"
)
  .form-grid(
    :data-label-position="labelPosition",
    :style="{ 'grid-template-columns': gridTemplateColumns }"
  )
    slot
  .form-actions.flex.mt-8(v-if="hasSlot('actions')")
    slot(name="actions")
</template>

<script setup lang="ts">
import { computed, provide, toRef, useSlots } from "vue";
import {
  smxFormLayoutKey,
  type SmxFormLabelPosition,
} from "@shared/components/elements/smxFormLayout";

const props = withDefaults(
  defineProps<{
    // Width of the fields
    width?: string;
    // Width of the labels
    labelWidth?: string;
    labelPosition?: SmxFormLabelPosition;
    columns?: 1 | 2;
  }>(),
  {
    width: "600px",
    labelPosition: "left",
    columns: 1,
  },
);

provide(smxFormLayoutKey, {
  labelPosition: toRef(props, "labelPosition"),
});

const gridTemplateColumns = computed(() => {
  if (props.labelPosition === "top") {
    return `repeat(${props.columns}, minmax(0, 1fr))`;
  }

  return `repeat(${props.columns}, ${props.labelWidth ?? "max-content"} minmax(0, ${props.width}))`;
});

const formWidth = computed(() => (props.columns === 2 ? "900px" : props.width));

const slots = useSlots();
function hasSlot(name: string) {
  return !!slots[name];
}
</script>

<style>
.form-grid {
  display: grid;
  row-gap: 1.2rem;
  column-gap: 1rem;
}

.smx-form-container {
  max-width: max-content;
}

.form-grid > * {
  min-height: 31px;
}

/* NOTE: This is to adjust the height with input elements */
.form-grid > .label,
.form-grid > p,
.form-grid > a,
.form-grid > div,
.form-grid > span {
  padding-top: 5px;
}

.form-grid > div:has(input) {
  padding-top: 0;
}

.form-grid > .el-checkbox {
  padding-top: 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.form-grid > .label {
  justify-content: flex-end;
  align-items: start;
  display: flex;
  max-width: 250px;
}

.form-grid[data-label-position="top"] > .smx-form-item {
  min-width: 0;
  min-height: 31px;
}

.form-grid[data-label-position="top"] {
  column-gap: 3rem;
}

.form-grid[data-label-position="top"] > .smx-form-item > .label {
  align-items: end;
  display: flex;
  max-width: none;
  padding-top: 0;
}

.form-grid[data-label-position="top"] > .smx-form-item > div {
  min-width: 0;
  padding-top: 0;
}

@media only screen and (max-width: 768px) {
  .form-grid[data-label-position="top"] > .smx-form-item > .label {
    align-items: start;
    flex-direction: column;
  }

  .form-grid[data-label-position="top"]
    > .smx-form-item
    > .label
    > .label-action {
    max-width: 100%;
  }
}

@media only screen and (max-width: 1000px) {
  .smx-form-container {
    width: calc(100% - 1rem) !important;
    max-width: calc(100% - 1rem) !important;
    margin-right: 1rem;
  }

  .form-grid {
    grid-template-columns: 1fr !important;
    row-gap: 0.2rem;
  }

  .form-grid > .label {
    justify-content: flex-start;
  }

  .form-actions {
    flex-direction: column-reverse;
  }

  .form-actions > * + * {
    margin-bottom: 1rem;
    margin-left: 0 !important;
  }
}
</style>
